import { FormControl, Validators } from '@angular/forms';
import { Injectable, Signal } from '@angular/core';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite,
  ParcelAppEntityParcelWrite,
  PropertyLegalTitleAppEntityDictionaryPropertyLegalTitle,
  PropertyLegalTitleService
} from '../../../../../../../openapi';
import { toSignal } from '@angular/core/rxjs-interop';
import { RoadControlsForm } from '../../../../common/components/road-controls-set/road-controls.form';
import { environment } from '../../../../../../../../environments/environment';

export type PurposeOfUseItem = 'Przemysłowy' | 'Usługowy' | 'Mieszkaniowy' | 'Rolniczy' | 'Inny' | string;

export type PostInvestmentUsageItem
  = 'Nie ulegnie zmianie'
  | 'Ulegnie zmianie na przemysłowy'
  | 'Ulegnie zmianie na usługowy'
  | 'Ulegnie zmianie na mieszkaniowy'
  | 'Ulegnie zmianie na inny cel'
  | '';

@Injectable()
export class LocationDataForm extends RoadControlsForm {

  public validators = Validators;

  public purposeOfUseList: PurposeOfUseItem[] = [
    'Przemysłowy',
    'Usługowy',
    'Mieszkaniowy',
    'Rolniczy',
    'Inny'
  ];

  public postInvestmentUsages: PostInvestmentUsageItem[] = [
    'Nie ulegnie zmianie',
    'Ulegnie zmianie na przemysłowy',
    'Ulegnie zmianie na usługowy',
    'Ulegnie zmianie na mieszkaniowy',
    'Ulegnie zmianie na inny cel'
  ];

  public propertyLegalTitles: Signal<PropertyLegalTitleAppEntityDictionaryPropertyLegalTitle[] | undefined> = toSignal(this.pr.getPropertyLegalTitleCollectionPropertyLegalTitleCollection({ pagination: false }));

  constructor(private pr: PropertyLegalTitleService) {
    super({
      propertyLegalTitle: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      purposeOfUse: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      postInvestmentUsage: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      referenceVehicleType: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      propertyParkingSpacesCount: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),

      geometry: new FormControl(null, environment.production ? Validators.required : []),

      parcels: new FormControl<ParcelAppEntityParcelWrite[]>(
        [], {
          nonNullable: true,
          validators: [Validators.required, Validators.minLength(1), Validators.maxLength(255)]
        }
      ),
      parcels2: new FormControl<ParcelAppEntityParcelWrite[]>(
        [], {
          nonNullable: true,
          validators: [Validators.required, Validators.minLength(1), Validators.maxLength(255)]
        }
      )
    });
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public propertyLegalTitleChange(v: string): void {
    if (v === '614eba03-8e27-46d0-8b0c-96fbf137460c') {
      this.addControl('propertyLegalTitleDesc', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    } else {
      this.removeControl('propertyLegalTitleDesc');
    }
  }

  public get propertyLegalTitleDesc(): FormControl | null {
    return this.get('propertyLegalTitleDesc') as FormControl | null;
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get parcels2(): FormControl {
    return this.get('parcels2') as FormControl;
  }

  public get parcelsValues(): ParcelAppEntityParcelWrite[] {
    return [...this.parcels2.value, ...this.parcels.value];
  }

  public get postInvestmentUsage(): FormControl | null {
    return this.get('postInvestmentUsage') as FormControl | null;
  }

  public get propertyParkingSpacesCount(): FormControl | null {
    return this.get('propertyParkingSpacesCount') as FormControl | null;
  }

  public override patchFormValues(app: ApplicationAppEntityApplicationRead): void {
    this.purposeOfUseChange(app.propertyUsagePurpose as PurposeOfUseItem);
    this.postInvestmentUsageChange(app.postInvestmentUsage as PostInvestmentUsageItem);

    super.patchFormValues({
      geometry: app?.geometry,
      roadName: app?.roadName || '',
      roadNumber: app?.roadNumber || '',
      street: app.street,
      precinct: app.precint,
      registrationNumber: app.registrationNumber,
      propertyLegalTitle: app.propertyLegalTitle?.id || '',
      propertyLegalTitleDesc: app?.propertyLegalTitleDesc || '',
      purposeOfUse: app.propertyUsagePurpose || '',
      postInvestmentUsage: app?.postInvestmentUsage || '',
      propertyUsageChange: app?.propertyUsageChange || '',
      decisionDate: app?.decisionDate,
      decisionNumber: app?.decisionNumber,
      referenceVehicleType: app?.referenceVehicleType || '',
      propertyParkingSpacesCount: app?.propertyParkingSpacesCount || ''
    });

    if (app?.propertyUsagePurposeText) {
      this.patchValue({ purposeOfUseDesc: app.propertyUsagePurposeText });
    }

    if (app?.parcels?.length) {
      this.parcels.setValue(app.parcels.filter(v => v.tag === 'parcels'));
      this.parcels2.setValue(app.parcels.filter(v => v.tag === 'parcels2'));
    }
  }

  public purposeOfUseChange(v: PurposeOfUseItem | string): void {
    if (v === 'Inny') {
      this.addControl('purposeOfUseDesc', new FormControl('', [Validators.required, Validators.maxLength(5000)]));
    } else {
      this.removeControl('purposeOfUseDesc');
    }
  }

  public postInvestmentUsageChange(v: PostInvestmentUsageItem | string): void {
    if (v === 'Ulegnie zmianie na inny cel') {
      this.addControl('propertyUsageChange', new FormControl('', [Validators.required, Validators.maxLength(5000)]));
    } else {
      this.removeControl('propertyUsageChange');
    }
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    return {
      geometry: this.geometry.value,

      roadName: this.roadName.value,
      roadNumber: this.roadNumber.value,
      street: this.street.value,

      precint: this.value?.precinct,
      registrationNumber: this.value.registrationNumber,
      propertyUsagePurpose: this.value.purposeOfUse,
      propertyUsagePurposeText: this.value?.purposeOfUseDesc || undefined,
      propertyLegalTitle: this.value?.propertyLegalTitle,
      propertyLegalTitleDesc: this.value?.propertyLegalTitleDesc,
      postInvestmentUsage: this.value?.postInvestmentUsage,
      propertyUsageChange: this.value?.propertyUsageChange,
      decisionDate: this.value?.decisionDate,
      decisionNumber: this.value?.decisionNumber,
      referenceVehicleType: this.value?.referenceVehicleType,
      propertyParkingSpacesCount: this.value?.propertyParkingSpacesCount ? +this.value?.propertyParkingSpacesCount : undefined
    };
  }
}
